
import { Component, Prop, Vue } from 'vue-property-decorator';
import { IInsightData, InsightType, InsightPriority, ResponseType, InsightAction } from '@/models/insight';
import InsightChart from './InsightChart.vue';
import InsightMetrics from './InsightMetrics.vue';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { CSVExportUtil, ICSVData } from '@/utils/csv-export';

@Component({
  components: {
    InsightChart,
    InsightMetrics,
  },
})
export default class InsightCard extends Vue {
  @Prop({ required: true })
  public insight!: IInsightData;

  @Prop({ default: false })
  public compact!: boolean;

  @Prop({ default: true })
  public showFooter!: boolean;

  @Prop({ default: false })
  public showDebugInfo!: boolean;

  // NEW: Track expanded state for tables
  public showAllItems: boolean = false;

  // Download functionality state
  public isDownloading: boolean = false;


  // =============================================================================
  // 🚨 EXPLICIT RESPONSE TYPE SUPPORT METHODS
  // =============================================================================

  get hasResponseTypeError(): boolean {
    return !!(
      this.insight.response_type && 
      (!this.insight.actual_type || this.insight.type_mismatch)
    );
  }

  public getResponseTypeErrorMessage(): string {
    if (!this.insight.response_type) {
      return 'No explicit response_type defined for this question';
    }
    if (!this.insight.actual_type) {
      return 'Backend did not return actual_type information';
    }
    if (this.insight.type_mismatch) {
      return `Type mismatch: expected ${this.insight.response_type}, got ${this.insight.actual_type}`;
    }
    return 'Unknown response type validation issue';
  }

  public getResponseTypeColor(): string {
    if (this.insight.type_mismatch) return 'error';
    if (this.insight.response_type && this.insight.actual_type) return 'success';
    return 'warning';
  }

  public getResponseTypeIcon(): string {
    if (this.insight.type_mismatch) return 'mdi-alert-circle';
    if (this.insight.response_type && this.insight.actual_type) return 'mdi-check-circle';
    return 'mdi-help-circle';
  }

  // =============================================================================
  // STRUCTURED DATA SUPPORT
  // =============================================================================

  get hasStructuredData(): boolean {
    return !!(this.insight.data && (
      this.insight.data.items ||
      this.insight.data.trends ||
      this.insight.data.comparisons ||
      this.insight.data.raw_data
    ));
  }

  get shouldShowMetrics(): boolean {
    const metricTypes: ResponseType[] = ['single_number', 'key_metrics'];
    return !!(
      this.insight.response_type && 
      metricTypes.includes(this.insight.response_type) &&
      (this.insight.data?.metrics || this.insight.value !== undefined)
    );
  }

  get transformedInsight(): IInsightData {
    // Transform key_metrics data to match frontend interface
    if (this.insight.response_type === 'key_metrics' && this.insight.data?.metrics) {
      const transformedMetrics = this.insight.data.metrics.map((metric: any) => ({
        name: metric.label || metric.name,  // Map "label" to "name"
        value: metric.value,
        unit: metric.unit,
        description: metric.description,
        trend: metric.trend,
        change: metric.change,
        changePercentage: metric.changePercentage
      }));

      return {
        ...this.insight,
        data: {
          ...this.insight.data,
          metrics: transformedMetrics
        }
      };
    }

    return this.insight;
  }

  get categoryDisplayName(): string {
    // Extract category name for display (category can be dict or string)
    if (this.insight.category && typeof this.insight.category === 'object') {
      return (this.insight.category as any).name || 'Insight';
    }
    return this.insight.category || 'Insight';
  }

  public getExecutionTime(): string {
    // Use actual execution time if available, otherwise default
    if (this.insight.execution_time_seconds) {
      return `${this.insight.execution_time_seconds}s`;
    }
    
    // Mock execution time for now - could be enhanced to use actual timing data
    return '8.5s';
  }

  public getMetricsLayout(): 'horizontal' | 'vertical' | 'grid' {
    if (this.compact) return 'horizontal';
    if (this.insight.response_type === 'key_metrics') return 'grid';
    return 'vertical';
  }

  // =============================================================================
  // CARD STYLING AND BEHAVIOR
  // =============================================================================

  public getCardClass(): string {
    const classes = [];
    
    if (this.insight.priority === 'HIGH') {
      classes.push('insight-card--high-priority');
    }
    
    if (this.insight.type === 'ALERT') {
      classes.push('insight-card--alert');
    }
    
    if (this.hasResponseTypeError) {
      classes.push('insight-card--error');
    }
    
    if (this.compact) {
      classes.push('insight-card--compact');
    }

    return classes.join(' ');
  }

  // =============================================================================
  // HELPER METHODS (from InsightPanel pattern)
  // =============================================================================

  public getInsightIcon(type: InsightType): string {
    const iconMap: Record<InsightType, string> = {
      ALERT: 'mdi-alert',
      RECOMMENDATION: 'mdi-lightbulb',
      METRIC: 'mdi-chart-line',
      TREND: 'mdi-trending-up',
      COMPARISON: 'mdi-compare',
      SUMMARY: 'mdi-format-list-bulleted'
    };
    return iconMap[type] || 'mdi-information';
  }

  public getInsightColor(type: InsightType, priority: InsightPriority): string {
    if (priority === 'HIGH') return 'error';
    if (priority === 'MEDIUM') return 'warning';
    
    const colorMap: Record<InsightType, string> = {
      ALERT: 'error',
      RECOMMENDATION: 'orange',
      METRIC: 'primary',
      TREND: 'success',
      COMPARISON: 'purple',
      SUMMARY: 'grey'
    };
    return colorMap[type] || 'primary';
  }

  public getIconColor(priority: InsightPriority): string {
    return priority === 'HIGH' ? 'white' : 'white';
  }

  public getPriorityColor(priority: InsightPriority): string {
    const colorMap: Record<InsightPriority, string> = {
      HIGH: 'error',
      MEDIUM: 'warning',
      LOW: 'success'
    };
    return colorMap[priority] || 'grey';
  }

  public getTrendIcon(trend: string): string {
    const iconMap: Record<string, string> = {
      up: 'mdi-trending-up',
      down: 'mdi-trending-down',
      stable: 'mdi-trending-neutral'
    };
    return iconMap[trend] || 'mdi-minus';
  }

  public getTrendColor(trend: string): string {
    const colorMap: Record<string, string> = {
      up: 'success',
      down: 'error',
      stable: 'warning'
    };
    return colorMap[trend] || 'grey';
  }

  public formatInsightValue(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(2);
  }

  public getInsightMessage(insight: IInsightData): string {
    return insight.insight || insight.message || insight.answer || '';
  }

  public isInsightError(insight: IInsightData): boolean {
    const message = this.getInsightMessage(insight);
    return message.includes('Unfortunately, I was not able to answer') || 
           message.includes('error') || 
           message.length === 0;
  }

  get hasAdditionalDetails(): boolean {
    return !!(
      this.insight.value ||
      this.insight.trend ||
      this.insight.recommendation
    );
  }

  // =============================================================================
  // DYNAMIC TABLE DATA METHODS
  // =============================================================================

  public getTableColumns(): Array<{key: string, header: string, align: string}> {
    // Analyze the actual data structure and create column definitions dynamically
    const firstItem = this.insight.data?.items?.[0];
    if (!firstItem) {
      return [];
    }

    const columns: Array<{key: string, header: string, align: string}> = [];
    
    // Get all property keys from the first item
    const keys = Object.keys(firstItem);
    
    for (const key of keys) {
      const value = firstItem[key];
      
      // Determine alignment based on data type
      const isNumeric = typeof value === 'number' || (!isNaN(Number(value)) && value !== '');
      const align = isNumeric ? 'right' : 'left';
      
      // Create readable header from key
      const header = this.createReadableHeader(key);
      
      columns.push({
        key,
        header,
        align
      });
    }

    // Sort columns: product name first, then others
    columns.sort((a, b) => {
      if (this.isProductNameColumn(a.key)) return -1;
      if (this.isProductNameColumn(b.key)) return 1;
      return 0;
    });

    return columns;
  }

  public getColumnValue(item: any, columnKey: string): string | number {
    const value = item[columnKey];
    
    // Handle empty/null values
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    // Apply intelligent formatting based on column type
    if (typeof value === 'number' || (!isNaN(Number(value)) && value !== '')) {
      const numericValue = typeof value === 'number' ? value : Number(value);
      
      // Detect column type and format accordingly
      if (this.isCurrencyColumn(columnKey)) {
        // Currency columns: use CurrencyHelper with merchant's currency
        return CurrencyHelper.formatCurrency(numericValue);
      } else if (this.isQuantityColumn(columnKey)) {
        // Quantity/integer columns: format with thousand separators, no decimals
        return numericValue.toLocaleString('en-US', {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        });
      } else {
        // General decimal numbers: format with thousand separators and 2 decimals
        return numericValue.toLocaleString('en-US', {
          maximumFractionDigits: 2,
          minimumFractionDigits: Number.isInteger(numericValue) ? 0 : 2
        });
      }
    }
    
    return value || '';
  }

  private createReadableHeader(key: string): string {
    // Remove brackets if present
    let header = key.replace(/^\[|\]$/g, '');
    
    // Handle special cases
    const headerMap: Record<string, string> = {
      'sold_quantity_param': 'Net Units Sold',
      'average_daily_units_sold_param': 'Units Sold/Day',
      'product_name': 'Product',
      'Item[Item Description]': 'Product',
      'vendor_lead_time': 'Lead Time (days)',
      'Actual Inventory Quantity': 'Current Stock',
      'stock_days_inventory_planning': 'Days of Stock',
      // NEW: Add mappings for the current API response columns
      'current_inventory': 'Current Stock',
      'days_remaining': 'Days Left',
      'units_sold_per_day': 'Units/Day'
    };
    
    if (headerMap[header]) {
      return headerMap[header];
    }
    
    // Convert snake_case to readable format
    return header
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  private isProductNameColumn(key: string): boolean {
    const productColumns = [
      'product_name',
      'Item[Item Description]',
      'name',
      'item_description',
      'variant'
    ];
    return productColumns.includes(key);
  }

  /**
   * Detect if a column contains currency/financial data based on column name patterns
   */
  private isCurrencyColumn(columnKey: string): boolean {
    const currencyKeywords = [
      'price', 'cost', 'revenue', 'sales', 'profit', 'amount', 'value',
      'total', 'subtotal', 'tax', 'fee', 'charge', 'payment', 'refund',
      'discount', 'commission', 'margin', 'budget', 'spend', 'expense',
      'income', 'earning', 'wage', 'salary', 'worth', 'balance'
    ];
    const lowercaseKey = columnKey.toLowerCase();
    return currencyKeywords.some(keyword => lowercaseKey.includes(keyword));
  }

  /**
   * Detect if a column contains quantity/count data that should be formatted as integers
   */
  private isQuantityColumn(columnKey: string): boolean {
    const quantityKeywords = [
      'quantity', 'qty', 'count', 'number', 'units', 'pieces', 'items',
      'stock', 'inventory', 'available', 'sold', 'ordered', 'shipped',
      'returned', 'days', 'weeks', 'months', 'years', 'age'
    ];
    const lowercaseKey = columnKey.toLowerCase();
    return quantityKeywords.some(keyword => lowercaseKey.includes(keyword));
  }

  // =============================================================================
  // ACTION HANDLING
  // =============================================================================

  public getActionColor(actionType: string): string {
    const colorMap: Record<string, string> = {
      filter: 'primary',
      navigate: 'info',
      export: 'success',
      create: 'warning',
      update: 'secondary'
    };
    return colorMap[actionType] || 'default';
  }

  public getActionIcon(actionType: string): string {
    const iconMap: Record<string, string> = {
      filter: 'mdi-filter',
      navigate: 'mdi-open-in-new',
      export: 'mdi-download',
      create: 'mdi-plus',
      update: 'mdi-pencil'
    };
    return iconMap[actionType] || 'mdi-cog';
  }

  public handleAction(action: InsightAction): void {
    this.$emit('action-clicked', action, this.insight);
    
    // Basic action handling
    switch (action.type) {
      case 'filter':
        this.$emit('apply-filter', action.params);
        break;
      case 'navigate':
        this.$emit('navigate', action.url);
        break;
      case 'export':
        this.$emit('export-data', action.params);
        break;
      default:
        console.log('Insight action clicked:', action);
    }
  }

  // NEW: Helper to determine if this is a numbered list (recommendations, actions, etc.)
  get isNumberedListType(): boolean {
    const categoryName = this.getCategoryName().toLowerCase();
    return (this.insight.response_type === 'list' && 
            !!(this.insight.data?.items && this.insight.data.items.length > 0)) ||
           (this.insight.response_type === 'data_table' && 
            categoryName.includes('recommended actions') &&
            !!(this.insight.data?.items && this.insight.data.items.length > 0));
  }

  // Helper to determine if the insight is a list-type (e.g., out of stock, inventory)
  get isListTypeInsight(): boolean {
    // Get category name properly (handle both string and object formats)
    const categoryName = this.getCategoryName().toLowerCase();
    
    // Check if this is a list-type insight that should show data as table rather than chart
    const listCategories = [
      'out of stock bestsellers',
      'out of stock',
      'inventory list',
      'product list',
      'low stock',
      'critical stock',
      'diagnostic insights' // Add diagnostic insights to always show as table
    ];
    
    const hasListCategory = listCategories.some(category => 
      categoryName.includes(category)
    );
    
    // SIMPLIFIED: Always show data_table response type as table (unless it's action lists)
    const isDataTable = this.insight.response_type === 'data_table' && 
                        !categoryName.includes('recommended actions');
    
    // Check if we have structured data with items
    const hasStructuredItems = !!(this.insight.data?.items && 
                                  this.insight.data.items.length > 0 && 
                                  this.insight.response_type !== 'list' && 
                                  !categoryName.includes('recommended actions'));
    
    return hasListCategory || isDataTable || hasStructuredItems;
  }

  private getCategoryName(): string {
    // Extract category name properly (handle both string and object formats)
    if (this.insight.category && typeof this.insight.category === 'object') {
      return (this.insight.category as any).name || '';
    }
    return this.insight.category || '';
  }

  // Helper to get display text for action items (handles both list and table formats)
  public getActionDisplayText(item: any): string {
    // For structured table data (Recommended Actions)
    const categoryName = this.getCategoryName().toLowerCase();
    if (categoryName.includes('recommended actions') && item.action_description) {
      const productName = item.product_name || 'Unknown Product';
      const currentInventory = item.current_inventory || '0';
      const reorderQuantity = item.reorder_quantity || '0';
      const businessImpact = item.business_impact || '';
      
      return `${item.action_description} for ${productName} (Current: ${currentInventory}, Reorder: ${reorderQuantity}) - ${businessImpact}`;
    }
    
    // For simple list format
    return item.content || item.text || item.action_description || item;
  }

  // Check if action item has structured data fields
  public isActionWithStructuredData(item: any): boolean {
    return !!(item.product_name || item.current_inventory || item.reorder_quantity || item.action_description);
  }

  // Format inventory values for display
  public formatInventoryValue(value: any): string {
    if (value === null || value === undefined) return '-';
    const numValue = Number(value);
    if (isNaN(numValue)) return String(value);
    
    // Format large numbers with commas
    return numValue.toLocaleString();
  }

  // Get color class for days left indicator
  public getDaysLeftColor(days: any): string {
    const numDays = Number(days);
    if (isNaN(numDays)) return '';
    
    if (numDays <= 5) return 'error--text';
    if (numDays <= 15) return 'warning--text'; 
    return 'success--text';
  }

  // =============================================================================
  // EXPANDABLE TABLE FUNCTIONALITY
  // =============================================================================

  get hasMoreItems(): boolean {
    return (this.insight.data?.items?.length || 0) > 5;
  }

  get remainingItemsCount(): number {
    return Math.max(0, (this.insight.data?.items?.length || 0) - 5);
  }

  public getDisplayedItems(): any[] {
    const items = this.insight.data?.items || [];
    return this.showAllItems ? items : items.slice(0, 5);
  }

  // Backend truncation support
  get isDataTruncatedByBackend(): boolean {
    return !!(this.insight.data?.metadata?.truncated);
  }

  public getBackendTotalRows(): number {
    return this.insight.data?.metadata?.total_rows || 0;
  }

  public getBackendReturnedRows(): number {
    return this.insight.data?.metadata?.returned_rows || this.insight.data?.items?.length || 0;
  }

  // =============================================================================
  // DOWNLOAD FUNCTIONALITY
  // =============================================================================

  get canDownload(): boolean {
    // Check if insight has downloadable data
    return !!(
      this.insight.data?.items?.length ||
      this.insight.data?.metrics?.length ||
      this.insight.value !== undefined ||
      this.insight.insight
    );
  }

  public async downloadAsCSV(): Promise<void> {
    this.isDownloading = true;
    
    try {
      const csvData = this.prepareCSVData();
      const filename = this.generateFilename();
      
      // Use CSVExportUtil to download the file
      CSVExportUtil.downloadCSVData(csvData, filename);
      
      // Emit success event
      this.$emit('download-success', {
        filename,
        rowCount: csvData.rows?.length || 1,
        responseType: this.insight.response_type
      });
      
    } catch (error) {
      console.error('Download failed:', error);
      this.$emit('download-error', error);
    } finally {
      this.isDownloading = false;
    }
  }

  private prepareCSVData(): ICSVData {
    const responseType = this.insight.response_type;
    
    switch (responseType) {
      case 'data_table':
        return this.prepareTableCSV();
      case 'key_metrics':
        return this.prepareMetricsCSV();
      case 'single_number':
        return this.prepareSingleValueCSV();
      case 'summary_text':
      case 'list':
        return this.prepareTextCSV();
      default:
        return this.prepareGenericCSV();
    }
  }

  private prepareTableCSV(): ICSVData {
    const items = this.insight.data?.items || [];
    
    if (items.length === 0) {
      return {
        headers: ['No Data'],
        rows: [['No data available']],
        metadata: this.collectMetadata()
      };
    }
    
    // Extract headers from first item (reuse existing table column logic)
    const columns = this.getTableColumns();
    const headers = columns.map(col => col.header);
    
    // Extract rows with RAW data (not formatted)
    const rows = items.map(item => 
      columns.map(col => {
        const rawValue = item[col.key];
        // Return raw data for CSV, not formatted display data
        return rawValue ?? '';
      })
    );
    
    return {
      headers,
      rows,
      metadata: this.collectMetadata()
    };
  }

  private prepareMetricsCSV(): ICSVData {
    const metrics = this.insight.data?.metrics || [];
    
    return {
      headers: ['Metric', 'Value', 'Unit'],
      rows: metrics.map(m => [
        m.name || (m as any).label,
        m.value,
        m.unit || ''
      ]),
      metadata: this.collectMetadata()
    };
  }

  private prepareSingleValueCSV(): ICSVData {
    return {
      headers: ['Question', 'Answer'],
      rows: [[
        this.insight.question || 'N/A',
        this.insight.value ?? this.insight.insight
      ]],
      metadata: this.collectMetadata()
    };
  }

  private prepareTextCSV(): ICSVData {
    const text = this.insight.insight || this.insight.message || '';
    
    return {
      headers: ['Insight'],
      rows: [[text]],
      metadata: this.collectMetadata()
    };
  }

  private prepareGenericCSV(): ICSVData {
    // Fallback for unknown response types
    const message = this.getInsightMessage(this.insight);
    return {
      headers: ['Content'],
      rows: [[message]],
      metadata: this.collectMetadata()
    };
  }

  private collectMetadata(): any {
    // Check if backend provided truncation metadata
    const backendMetadata = this.insight.data?.metadata || {};
    const itemsLength = this.insight.data?.items?.length || 1;
    
    return {
      question: this.insight.question || 'Unknown Question',
      instruction: this.getInstructionText(),
      generatedAt: new Date().toISOString(),
      source: this.getCategoryName() || 'insight',
      responseType: this.insight.response_type,
      // Use backend metadata if available, otherwise default to false
      truncated: backendMetadata.truncated || false,
      totalRows: backendMetadata.total_rows || itemsLength,
      returnedRows: backendMetadata.returned_rows || itemsLength,
      maxRowsLimit: backendMetadata.max_rows_limit || 100
    };
  }

  private getInstructionText(): string {
    // Try to get instruction from various sources
    return (this.insight as any).instruction || 
           (this.insight as any).prompt || 
           this.insight.question || 
           'AI analysis of report data';
  }

  private generateFilename(): string {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const category = this.getCategoryName() || 'insight';
    const sanitized = category.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    return `${sanitized}_${timestamp}.csv`;
  }


}
